'use client'

import type { ReactNode } from 'react'

import './accordion.css'

import { useEffect, useMemo, useRef, useState } from 'react'
import Accordion from 'react-bootstrap/Accordion'

import { LinkButton } from '@/components/buttons'
import JsonLd from '@/components/seo/JsonLd'
import useStore from '@/stores/Store'
import { parseCmsHtml } from '@/utils'
import { faqPageSchema } from '@/utils/schema'

interface AccordionSimpleProps {
  data?: { title?: string; content?: string; description?: string }[]
  toggleButton?: ReactNode
  viewAllButton?: ReactNode
  // Emit FAQPage JSON-LD from the rendered items. Opt-in so non-FAQ accordions
  // (notices, procedures) are not mislabelled as FAQs.
  faqSchema?: boolean
}

function AccordionSimple({
  data = [],
  toggleButton,
  viewAllButton,
  faqSchema = false
}: AccordionSimpleProps) {
  const { setFaqRef } = useStore()
  const ref = useRef<HTMLDivElement>(null)

  // Build the FAQPage node from the same raw data the accordion renders, so the
  // markup can never drift from what the user sees.
  const faqSchemaData = useMemo(
    () =>
      faqSchema
        ? faqPageSchema(
            data.map(item => ({
              question: item?.title ?? '',
              answer: item?.content ?? item?.description ?? ''
            }))
          )
        : null,
    [faqSchema, data]
  )

  const [activeKeys, setActiveKeys] = useState<string[]>([])
  const [viewMore, setViewMore] = useState(false)

  // Derived, not state: `toggle` was kept in a useState synced by an effect on
  // `activeKeys`, which forced a second render on every tap. It is fully
  // determined by whether every item is open, so compute it inline instead.
  const allExpanded = activeKeys.length === data.length

  // Parse each item's CMS HTML once per `data` change instead of on every
  // render. Toggling an accordion updates `activeKeys`, which re-renders the
  // whole list; without this memo every title/content/description would be
  // re-parsed by html-react-parser on every tap — the main mobile lag source.
  const parsedItems = useMemo(
    () =>
      data.map(item => ({
        title: item?.title ? parseCmsHtml(item.title) : null,
        content: item?.content ? parseCmsHtml(item.content) : null,
        description: item?.description ? parseCmsHtml(item.description) : null
      })),
    [data]
  )

  const itemsToShow = viewMore ? parsedItems : parsedItems.slice(0, 8)

  const toggleAll = expand => {
    setActiveKeys(expand ? data.map((_, index) => index.toString()) : [])
  }

  const handleSelect = eventKey => {
    setActiveKeys(prev =>
      prev.includes(eventKey) ? prev.filter(key => key !== eventKey) : [...prev, eventKey]
    )
  }

  useEffect(() => {
    setFaqRef(ref)
  }, [])

  return (
    <div id="accordion-common">
      {faqSchemaData && <JsonLd data={faqSchemaData} />}
      {toggleButton && (
        <div className="mb-2 d-flex justify-end">
          {!allExpanded ? (
            <span className="toggle-button" onClick={() => toggleAll(true)}>
              Expand all
            </span>
          ) : (
            <span className="toggle-button" onClick={() => toggleAll(false)}>
              Collapse all
            </span>
          )}
        </div>
      )}
      <Accordion
        id="accordion-simple"
        activeKey={activeKeys}
        onSelect={handleSelect}
        data-aos="fade-up"
        ref={ref}
      >
        {itemsToShow.map((item, index) => (
          <Accordion.Item
            key={index}
            className="accordion-item-wrapper"
            eventKey={index.toString()}
          >
            <Accordion.Header as="h3">
              <div className="d-inline-flex align-items-center lh-base">{item.title}</div>
            </Accordion.Header>
            {item.content && <Accordion.Body>{item.content}</Accordion.Body>}
            {item.description && <Accordion.Body>{item.description}</Accordion.Body>}
          </Accordion.Item>
        ))}
      </Accordion>

      {viewAllButton &&
        (!viewMore ? (
          <div className="d-flex justify-content-center my-5">
            <LinkButton text="View all FAQ" onClick={() => setViewMore(true)}></LinkButton>
          </div>
        ) : (
          <div className="d-flex justify-content-center my-5">
            <LinkButton
              text="View fewer FAQ"
              onClick={() => {
                setViewMore(false)
                window.scrollTo({
                  top: (ref.current?.offsetTop ?? 0) - 400,
                  behavior: 'smooth'
                })
              }}
            ></LinkButton>
          </div>
        ))}
    </div>
  )
}

export default AccordionSimple
